import { EditIcon } from '@chakra-ui/icons';
import {
  Box,
  Switch,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  IconButton,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { FinancialData, Util } from '@finley-technologies/finley-types';

import {
  CreateFinStatementLineItemInputGQLType,
  FinancialStatementAdHocCalculationsGQLType,
  UpdateFinStatementLineItemInputGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import {
  GetFinancialStatementDataByDateRangeQueryGQLType,
  useCreateFinStatementLineItemMutation,
  useDeleteFinStatementLineItemMutation,
  useDeleteFinStatementMutation,
  useUpdateFinStatementLineItemMutation,
} from '../../requests/financialData.gql.generated';
import { CsvXlsxDownloadSelect } from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { errorToastConfig } from 'packages/common-frontend/constants';
import {
  numberToFormattedRounded,
  numberWithCommas,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import { GET_FINANCIAL_STATEMENT_DATA_BY_DATE_RANGE } from 'packages/financial-data/requests/financialData.gql';
import { CreateFinStatementLineItemModal } from 'packages/financial-data/ui/financialStatements/CreateFinStatementLineItemModal';
import { EditFinStatementLineItemModal } from 'packages/financial-data/ui/financialStatements/EditFinStatementLineItemModal';
import { colors } from '~/packages/common-frontend/colors';

// helpers
export const getFinancialStatementsChartAndTableProps = (
  financialStatement: FinancialData.FinancialStatementType,
  selectedReportFrequency: FinancialData.ReportFrequency,
  organizationId: string,
  chartDataDateRange: {
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  },
  selectedSectionName:
    | FinancialData.BalanceSheetSectionTypes
    | FinancialData.IncomeStatementSectionTypes,
  creditFacilityId: string,
) => {
  return {
    organizationId,
    financialStatement,
    reportFrequency: selectedReportFrequency,
    startDate: commonDate.dateToFullDateString(
      chartDataDateRange.chartDataStartDate,
    ),
    endDate: commonDate.dateToFullDateString(
      chartDataDateRange.chartDataEndDate,
    ),
    selectedSectionName,
    creditFacilityId,
  };
};

const getSectionKeyPlaceholder = (
  financialStatement: FinancialData.FinancialStatementType,
) => {
  switch (financialStatement) {
    case 'balanceSheet':
      return 'assets';
    case 'incomeStatement':
      return 'income';
    default:
      return finleyAssertUnreachable(financialStatement);
  }
};

type GetFinancialStatementsChartAndTableProps = ReturnType<
  typeof getFinancialStatementsChartAndTableProps
>;

// helpers
const dateToCadencePeriod = (
  inputDate: Util.FullDateStr,
  reportFrequency: FinancialData.ReportFrequency,
) => {
  const date = commonDate.fullDateStrToDate(inputDate);
  const year = date.getFullYear();
  if (reportFrequency === 'month') {
    const month = date.toLocaleString('default', { month: 'long' });
    const day = date.getDate();
    return `${month} ${day}, ${year}`;
  }
  if (reportFrequency === 'quarter') {
    const quarter = Math.floor((date.getMonth() + 3) / 3);
    return `Q${quarter} ${year}`;
  }
  return `${year}`;
};

const getDeltaCadenceLabel = (
  reportFrequency: FinancialData.ReportFrequency,
) => {
  const cadenceMap = {
    month: ' M/M',
    quarter: ' Q/Q',
    year: ' Y/Y',
  };
  return `Δ ${cadenceMap[reportFrequency]}`;
};

const formatCellValue = (
  cellValue: number | null,
  formatOptions: { isPercentage?: boolean; showFullValue?: boolean },
) => {
  const { showFullValue, isPercentage } = formatOptions;
  if (isPercentage) {
    return cellValue === null
      ? '-%'
      : percentageToPercentageLabel(cellValue * 100, 2);
  }

  if (cellValue === null) return '-';
  return showFullValue
    ? `$${numberWithCommas(cellValue.toFixed(0))}`
    : `$${numberToFormattedRounded(cellValue)}`;
};

const getCellColor = (value: number | null | undefined) => {
  if (!value) return undefined;
  return value > 0 ? colors.green[400] : colors.red[400];
};

const stickyLeftColStyling = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 1,
  backgroundColor: colors.gray[100],
};

type FinancialStatementsTableProps = {
  financialStatement: FinancialData.FinancialStatementType;
  reportFrequency: FinancialData.ReportFrequency;
  financialStatementData: GetFinancialStatementDataByDateRangeQueryGQLType['getFinancialStatementDataByDateRange'];
  queryProps: GetFinancialStatementsChartAndTableProps;
};

export const FinancialStatementsTable = ({
  financialStatement,
  reportFrequency,
  financialStatementData,
  queryProps,
}: FinancialStatementsTableProps) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [
    lineItemEdits,
    setLineItemEdits,
  ] = useState<UpdateFinStatementLineItemInputGQLType | null>(null);
  const [
    createLineItemData,
    setCreateLineItemData,
  ] = useState<CreateFinStatementLineItemInputGQLType | null>(null);
  const [showFullValue, setShowFullValue] = useState<boolean>(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [previousWindowWidth, setPreviousWindowWidth] = useState<number>(
    window.innerWidth,
  );

  const [updateFinStatementLineItem] = useUpdateFinStatementLineItemMutation({
    refetchQueries: [
      {
        query: GET_FINANCIAL_STATEMENT_DATA_BY_DATE_RANGE,
        variables: queryProps,
      },
    ],
    onError: () => {
      toast({
        ...errorToastConfig,
        title:
          'Unable to update financial statement record. Please see technical help.',
      });
      onClose();
    },
  });

  const [deleteFinStatementLineItem] = useDeleteFinStatementLineItemMutation({
    refetchQueries: [
      {
        query: GET_FINANCIAL_STATEMENT_DATA_BY_DATE_RANGE,
        variables: queryProps,
      },
    ],
    onError: () => {
      toast({
        ...errorToastConfig,
        title:
          'Unable to delete financial statement record. Please see technical help.',
      });
      onClose();
    },
  });

  const [createFinStatementLineItem] = useCreateFinStatementLineItemMutation({
    refetchQueries: [
      {
        query: GET_FINANCIAL_STATEMENT_DATA_BY_DATE_RANGE,
        variables: queryProps,
      },
    ],
    onError: () => {
      toast({
        ...errorToastConfig,
        title:
          'Unable to create financial statement record. Please see technical help.',
      });
      onCreateClose();
    },
  });

  const [deleteFinStatement] = useDeleteFinStatementMutation({
    refetchQueries: [
      {
        query: GET_FINANCIAL_STATEMENT_DATA_BY_DATE_RANGE,
        variables: queryProps,
      },
    ],
    onError: () => {
      toast({
        ...errorToastConfig,
        title:
          'Unable to delete financial statement. Please see technical help.',
      });
      onCreateClose();
    },
  });

  useEffect(() => {
    const onResize = () => {
      const currentWindowWidth = window.innerWidth;
      if (
        tableContainerRef.current &&
        previousWindowWidth > currentWindowWidth
      ) {
        tableContainerRef.current.scrollLeft +=
          previousWindowWidth - currentWindowWidth;
      }
      setPreviousWindowWidth(currentWindowWidth);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [previousWindowWidth]);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollLeft =
        tableContainerRef.current.scrollWidth;
    }
  }, [financialStatementData]);

  if (!financialStatementData) {
    return (
      <Text>
        No records were found for the selected date range and report frequency.
        Adjust your selection.
      </Text>
    );
  }

  const endOfPeriodDatesAndCadenceDeltas = financialStatementData.metadata.sortedEndOfPeriodDates.flatMap(
    (endOfPeriodDate, idx) =>
      idx > 0
        ? [
            getDeltaCadenceLabel(reportFrequency),
            dateToCadencePeriod(endOfPeriodDate, reportFrequency),
          ]
        : [dateToCadencePeriod(endOfPeriodDate, reportFrequency)],
  );

  const netIncomeAdHocData: FinancialStatementAdHocCalculationsGQLType = financialStatementData.adHocCalculations.find(
    (sectionTotal) => sectionTotal.adHocKey === 'netIncome',
  ) ?? {
    adHocKey: 'netIncome',
    totals: [],
  };

  const headers = [
    { label: 'Label', key: 'label' },
    { label: 'Ledger', key: 'ledger' },
    ...financialStatementData.metadata.sortedEndOfPeriodDates.map((header) => ({
      label: dateToCadencePeriod(header, reportFrequency),
      key: dateToCadencePeriod(header, reportFrequency), // dynamic keys
    })),
  ];

  const dataRows = financialStatementData.sectionKeysToLineItems.flatMap(
    (lineItems) => {
      return lineItems.lineItemsToValues.map((lineItemsToValue) => {
        const row: Record<string, string> = {
          label: lineItems.finleySectionKey,
          ledger: lineItemsToValue.lineItemDescription,
        };

        financialStatementData.metadata.sortedEndOfPeriodDates.forEach(
          (__, idx) => {
            row[
              dateToCadencePeriod(
                financialStatementData.metadata.sortedEndOfPeriodDates[idx],
                reportFrequency,
              )
            ] = String(lineItemsToValue.values[idx]?.value ?? '');
          },
        );

        return row;
      });
    },
  );

  return (
    <>
      <Flex alignItems="center" mt="8" mb="4">
        <Text mr="2" fontWeight="bold">
          Show full value format
        </Text>
        <Switch
          size="sm"
          isChecked={showFullValue}
          onChange={() => setShowFullValue(!showFullValue)}
          _focus={{ boxShadow: 'none' }}
        />
        <CsvXlsxDownloadSelect
          dataRows={dataRows}
          headers={headers}
          filenamePrefix="financial-statement"
          MenuButtonProps={{ ml: '24px' }}
        />

        <Flex ml="auto" alignItems="center">
          <Text mr="2" fontWeight="bold">
            Edit
          </Text>
          <Switch
            size="sm"
            isChecked={editMode}
            onChange={() => setEditMode(!editMode)}
            _focus={{ boxShadow: 'none' }}
          />
        </Flex>
      </Flex>

      <Box
        borderWidth="thin"
        borderColor={colors.gray[100]}
        overflowX="auto"
        overflowY="auto"
        position="relative"
        ref={tableContainerRef}
      >
        <Table variant="simple">
          <Thead>
            <Tr borderBottom={`2px solid ${colors.black[80]}`} height="52px">
              <Th
                backgroundColor={colors.background[200]}
                style={{
                  ...stickyLeftColStyling,
                  backgroundColor: colors.background[200],
                }}
              />
              {endOfPeriodDatesAndCadenceDeltas.map(
                (endOfPeriodDateOrCadenceDelta, idx) => {
                  // Check if this is a delta column (odd indices) or date column (even indices)
                  const isDateColumn = idx % 2 === 0;
                  // Calculate the actual date index for non-delta columns
                  const dateIndex = Math.floor(idx / 2);

                  return (
                    <Th
                      whiteSpace="nowrap"
                      key={
                        idx > 0
                          ? `${endOfPeriodDateOrCadenceDelta}-
                          ${endOfPeriodDatesAndCadenceDeltas[idx - 1]}`
                          : endOfPeriodDateOrCadenceDelta
                      }
                      backgroundColor={colors.background[200]}
                      color={colors.black[95]}
                      isNumeric
                      position="relative"
                    >
                      <Box
                        role="group"
                        display="inline-block"
                        position="relative"
                      >
                        {endOfPeriodDateOrCadenceDelta}
                        {editMode && isDateColumn && (
                          <IconButton
                            aria-label="Create line item for this date"
                            icon={<EditIcon />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            position="absolute"
                            top="50%"
                            right="-24px"
                            transform="translateY(-50%)"
                            opacity={0}
                            _groupHover={{ opacity: 1 }}
                            transition="opacity 0.2s ease-in-out"
                            onClick={() => {
                              // Find a finStatementId for this date index
                              // We'll use the first line item's value for this date to get the finStatementId
                              const firstSectionWithData = financialStatementData.sectionKeysToLineItems.find(
                                (section) =>
                                  section.lineItemsToValues.length > 0,
                              );

                              if (
                                firstSectionWithData &&
                                firstSectionWithData.lineItemsToValues[0]
                                  ?.values[dateIndex]
                              ) {
                                const finStatementId =
                                  firstSectionWithData.lineItemsToValues[0]
                                    .values[dateIndex].finStatementId;

                                setCreateLineItemData({
                                  finStatementId,
                                  ledgerName: 'PLACEHOLDER',
                                  section: getSectionKeyPlaceholder(
                                    financialStatement,
                                  ),
                                  value: 0,
                                });
                                onCreateOpen();
                              }
                            }}
                          />
                        )}
                      </Box>
                    </Th>
                  );
                },
              )}
            </Tr>
          </Thead>
          <Tbody>
            {financialStatementData.sectionKeysToLineItems.map(
              (sectionData) => (
                <React.Fragment key={sectionData.finleySectionKey}>
                  <Tr
                    bg={colors.gray[100]}
                    fontWeight="bold"
                    borderTop={`2px solid ${colors.black[80]}`}
                  >
                    <Td style={stickyLeftColStyling}>
                      {_.capitalize(sectionData.finleySectionKey)}
                    </Td>
                    <Td colSpan={endOfPeriodDatesAndCadenceDeltas.length} />
                  </Tr>
                  {sectionData.lineItemsToValues.map((lineItem) => (
                    <Tr key={lineItem.lineItemDescription} bg={colors.white}>
                      <Td
                        pl={20}
                        style={{
                          ...stickyLeftColStyling,
                          backgroundColor: colors.white,
                        }}
                      >
                        {lineItem.lineItemDescription}
                      </Td>
                      {lineItem.values.map((value, idx) => (
                        <React.Fragment
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${value.finStatementLineItemId}-${idx}`}
                        >
                          {idx > 0 && (
                            <Td
                              isNumeric
                              style={{
                                color: getCellColor(value.cadenceDelta),
                              }}
                            >
                              {formatCellValue(value.cadenceDelta ?? null, {
                                isPercentage: true,
                              })}
                            </Td>
                          )}
                          <Td isNumeric position="relative">
                            <Box
                              role="group" // enables hover state for children
                              display="inline-block"
                              position="relative"
                            >
                              <Text>
                                {formatCellValue(value.value ?? null, {
                                  isPercentage: false,
                                  showFullValue,
                                })}
                              </Text>
                              {editMode && (
                                <IconButton
                                  aria-label={
                                    value.finStatementLineItemId
                                      ? 'Edit value'
                                      : 'Create value'
                                  }
                                  icon={<EditIcon />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  position="absolute"
                                  top="50%"
                                  right="-24px"
                                  transform="translateY(-50%)"
                                  opacity={0}
                                  _groupHover={{ opacity: 1 }} // show on hover
                                  transition="opacity 0.2s ease-in-out"
                                  onClick={() => {
                                    if (value.finStatementLineItemId) {
                                      // Edit existing line item
                                      setLineItemEdits({
                                        finStatementLineItemId:
                                          value.finStatementLineItemId,
                                        props: {
                                          value: value.value,
                                          ledgerName:
                                            lineItem.lineItemDescription,
                                        },
                                      });
                                      onOpen();
                                    } else {
                                      // Create new line item
                                      setCreateLineItemData({
                                        finStatementId: value.finStatementId,
                                        ledgerName:
                                          lineItem.lineItemDescription,
                                        section: sectionData.finleySectionKey,
                                        value: 0,
                                      });
                                      onCreateOpen();
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          </Td>
                        </React.Fragment>
                      ))}
                    </Tr>
                  ))}
                  <Tr
                    bg={colors.gray[100]}
                    borderBottom={`2px solid ${colors.black[80]}`}
                    fontWeight="bold"
                  >
                    <Td pl={20} style={stickyLeftColStyling}>
                      Total
                    </Td>
                    {sectionData.totals.map((total, idx) => {
                      return (
                        <React.Fragment key={total.endOfPeriodDate}>
                          {idx > 0 && (
                            <Td
                              isNumeric
                              style={{
                                color: getCellColor(total.cadenceDelta),
                              }}
                            >
                              {formatCellValue(total.cadenceDelta ?? null, {
                                isPercentage: true,
                              })}
                            </Td>
                          )}
                          <Td isNumeric>
                            {formatCellValue(total.value ?? null, {
                              isPercentage: false,
                              showFullValue,
                            })}
                          </Td>
                        </React.Fragment>
                      );
                    })}
                  </Tr>
                </React.Fragment>
              ),
            )}
            {financialStatement === 'incomeStatement' && (
              <Tr
                bg={colors.gray[100]}
                borderBottom={`2px solid ${colors.black[80]}`}
                fontWeight="bold"
              >
                <Td style={stickyLeftColStyling}>Net Income</Td>
                {netIncomeAdHocData.totals.map((total, idx) => {
                  return (
                    <React.Fragment key={total.endOfPeriodDate}>
                      {idx > 0 && (
                        <Td
                          isNumeric
                          style={{ color: getCellColor(total.cadenceDelta) }}
                        >
                          {formatCellValue(total.cadenceDelta ?? null, {
                            isPercentage: true,
                          })}
                        </Td>
                      )}
                      <Td isNumeric>
                        {formatCellValue(total.value ?? null, {
                          isPercentage: false,
                          showFullValue,
                        })}
                      </Td>
                    </React.Fragment>
                  );
                })}
              </Tr>
            )}
          </Tbody>
        </Table>
        {lineItemEdits && (
          <EditFinStatementLineItemModal
            isOpen={isOpen}
            onCancel={onClose}
            onConfirm={async () => {
              await updateFinStatementLineItem({
                variables: {
                  updateInput: {
                    finStatementLineItemId:
                      lineItemEdits.finStatementLineItemId,
                    props: lineItemEdits.props,
                  },
                },
              });
            }}
            onDelete={async () => {
              await deleteFinStatementLineItem({
                variables: {
                  finStatementLineItemId: lineItemEdits.finStatementLineItemId,
                },
              });
            }}
            lineItemEdits={lineItemEdits}
            setLineItemEdits={setLineItemEdits}
          />
        )}
        {createLineItemData && (
          <CreateFinStatementLineItemModal
            isOpen={isCreateOpen}
            onCancel={onCreateClose}
            onConfirm={async () => {
              await createFinStatementLineItem({
                variables: {
                  finStatementLineItem: createLineItemData,
                },
              });
            }}
            onDeleteFinStatement={async () => {
              await deleteFinStatement({
                variables: {
                  finStatementId: createLineItemData.finStatementId,
                },
              });
            }}
            lineItemData={createLineItemData}
            setLineItemData={setCreateLineItemData}
            financialStatement={financialStatement}
          />
        )}
      </Box>
    </>
  );
};
